import type { JSX } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { CHART_COLOURS } from "../utils/taxUtils";
import type { BreakdownData } from "../types";

interface BreakdownChartProps {
  data: BreakdownData[];
  title?: string;
  description?: string;
  warnings?: string[];
  headerSlot?: JSX.Element;
  highlight?: boolean;
  className?: string;
}

export function BreakdownChart({
  data,
  title,
  description,
  warnings = [],
  headerSlot,
  highlight = false,
  className = "",
}: BreakdownChartProps): JSX.Element {
  const total = data.reduce((sum, { value }) => sum + value, 0);

  const currencyFormatter = new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
  });

  const containerClassName = `bg-white shadow rounded-lg overflow-hidden border ${
    highlight ? "border-gray-400 ring-2 ring-gray-200" : "border-gray-200"
  } ${className}`.trim();

  return (
    <div className={containerClassName}>
      {headerSlot}

      <div className="p-6 space-y-4">
      {/* Title & description */}
      {title && <h2 className="text-xl font-semibold">{title}</h2>}
      {description && <p className="text-sm text-gray-600">{description}</p>}

      {/* Chart & legend */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
        <div style={{ height: 300, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                innerRadius={90}
                outerRadius={120}
                paddingAngle={4}
              >
                {data.map((entry, i) => (
                  <Cell
                    key={entry.name}
                    fill={CHART_COLOURS[i % CHART_COLOURS.length]}
                  />
                ))}
              </Pie>
              <Tooltip formatter={(value: number) => currencyFormatter.format(value)} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <ul className="space-y-2">
          {data.map((entry, i) => {
            const colour = CHART_COLOURS[i % CHART_COLOURS.length];
            const formattedValue = currencyFormatter.format(entry.value);
            const percentage =
              total > 0 ? `${((entry.value / total) * 100).toFixed(1)}%` : "0%";

            return (
              <li key={entry.name} className="flex items-center justify-between">
                <div className="flex items-center">
                  <span
                    className="w-3 h-3 rounded-full mr-2"
                    style={{ backgroundColor: colour }}
                  />
                  <span className="font-medium">{entry.name}</span>
                </div>
                <div className="text-right">
                  <div>{formattedValue}</div>
                  <div className="text-sm text-gray-500">{percentage}</div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Warnings */}
        {warnings.length > 0 && (
          <div className="space-y-1">
            {warnings.map((msg, idx) => (
              <p key={idx} className="text-yellow-600">
                ⚠ {msg}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
